import { useState, useEffect } from "react";
import LogoutButton from "./auth/LogoutButton";
import DriverApplicationForm from "./forms/DriverApplicationForm";
import { getAccessToken } from "./auth/DriverAuth";
import api from "./utils/axiosWithInterceptor";
import PayfastForm from "./PayfastForm/PayfastForm";

export default function DriverDashboard() {
  const [isDriver, setIsDriver] = useState(false);
  const [driverData, setDriverData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Add these states to DriverDashboard
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    id_number: driverData?.id_number || "",
    car_plate_number: driverData?.car_plate_number || "",
    car_model: driverData?.car_model || "",
    phone_number: driverData?.phone_number || "",
    physical_address: driverData?.physical_address || "",
    license_image: null,
    id_image: null,
  });

  const handleDetailsUpdate = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    // Separate API calls for images and text data
    try {
      if (editForm.license_image || editForm.id_image) {
        const imageFormData = new FormData();
        if (editForm.license_image)
          imageFormData.append("license_image", editForm.license_image);
        if (editForm.id_image)
          imageFormData.append("id_image", editForm.id_image);

        await fetch(
          `https://werideapp-backend-production.up.railway.app/driver/driver-profile/${driverData.id}/details`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${getAccessToken()}` },
            body: imageFormData,
          }
        );
      }

      // Separate call for text data
      if (
        editForm.id_number !== driverData.id_number ||
        editForm.car_plate_number !== driverData.car_plate_number ||
        editForm.car_model !== driverData.car_model ||
        editForm.phone_number !== driverData.phone_number ||
        editForm.physical_address !== driverData.physical_address
      ) {
        const queryParams = new URLSearchParams({
          id_number: editForm.id_number,
          car_plate_number: editForm.car_plate_number,
          car_model: editForm.car_model,
          phone_number: editForm.phone_number,
          physical_address: editForm.physical_address,
        }).toString();

        await fetch(
          `https://werideapp-backend-production.up.railway.app/driver/driver-profile/${driverData.id}/details?${queryParams}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
          }
        );
      }

      window.location.reload();
    } catch (error) {
      console.error("Update failed:", error);
      alert("Failed to update details");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (driverData) {
      setEditForm({
        id_number: driverData.id_number,
        car_plate_number: driverData.car_plate_number,
        car_model: driverData.car_model,
        phone_number: driverData.phone_number,
        physical_address: driverData.physical_address,
        license_image: null,
        id_image: null,
      });
    }
  }, [driverData]);

  // Add this component for image update UI
  function CarImageUpdate({ index, currentUrl, profileId, onUpdate }) {
    const [loading, setLoading] = useState(false);

    const handleImageUpdate = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setLoading(true);
      const formData = new FormData();
      formData.append(`image_${index + 1}`, file);

      try {
        const response = await fetch(
          `https://werideapp-backend-production.up.railway.app/driver/driver-profile/${profileId}/car-images`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${getAccessToken()}` },
            body: formData,
          }
        );

        if (response.ok) {
          onUpdate();
        }
      } catch (error) {
        console.error("Failed to update image:", error);
      }
      setLoading(false);
    };

    return (
      <div className="relative group">
        <img
          src={currentUrl}
          alt={`Car view ${index + 1}`}
          className="w-full h-48 object-cover rounded-lg bg-gray-100"
        />
        <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-lg">
          <span className="text-white text-sm">Update Image</span>
          <input
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleImageUpdate}
          />
        </label>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-white"></div>
          </div>
        )}
      </div>
    );
  }

  //Fetch driver profile data
  useEffect(() => {
    const fetchDriverProfile = async () => {
      try {
        const response = await api.get("/driver/driver-profile/me");
        setDriverData(response.data);
        console.log(response.data);
        setIsDriver(true);
      } catch (error) {
        setIsDriver(false);
      } finally {
        setLoading(false);
      }
    };

    fetchDriverProfile();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="relative w-24 h-24">
          <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-emerald-500 rounded-full animate-spin border-t-transparent"></div>
          <div className="absolute inset-4 border-4 border-black rounded-full animate-[spin_3s_linear_infinite] border-t-transparent"></div>
          <div className="absolute inset-8 border-4 border-emerald-500 rounded-full animate-[spin_2s_linear_infinite] border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!isDriver) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <LogoutButton />
        <div className="max-w-3xl mx-auto">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <p className="text-yellow-700 font-medium">
              You don't have a driver profile yet
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Become a Driver
            </h2>

            <DriverApplicationForm onSuccess={() => { setIsDriver(true); window.location.reload(); }} />
          </div>
        </div>
      </div>
    );
  }

  // Display driver data

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <LogoutButton />
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Status Banner */}
        <div className="bg-emerald-50 border-l-4 border-emerald-400 p-4">
          <p className="text-emerald-700 font-medium">
            Verification Status:{" "}
            {driverData.is_verified ? (
              <span className="text-emerald-600">Verified ✓</span>
            ) : (
              <span className="text-yellow-600">Pending</span>
            )}
          </p>
        </div>

        {/* Driver Info Section */}
        {/* <PayfastForm /> */}

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              Driver Information
            </h2>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="px-4 py-2 text-sm rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
            >
              {isEditing ? "Cancel" : "Edit Details"}
            </button>
          </div>

          {isEditing ? (
            <form onSubmit={handleDetailsUpdate} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-4">
                    Update Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        ID Number
                      </label>
                      <input
                        type="text"
                        value={editForm.id_number}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            id_number: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Car Plate Number
                      </label>
                      <input
                        type="text"
                        value={editForm.car_plate_number}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            car_plate_number: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Car Model
                      </label>
                      <input
                        type="text"
                        value={editForm.car_model}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            car_model: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        value={editForm.phone_number}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            phone_number: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Physical Address
                      </label>
                      <input
                        type="text"
                        value={editForm.physical_address}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            physical_address: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-4">
                    Update Documents
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        License Document
                      </label>
                      <input
                        type="file"
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            license_image: e.target.files[0],
                          })
                        }
                        accept="image/*"
                        className="mt-1 block w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        ID Document
                      </label>
                      <input
                        type="file"
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            id_image: e.target.files[0],
                          })
                        }
                        accept="image/*"
                        className="mt-1 block w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={isSaving}
                className="w-full py-2 px-4 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:bg-emerald-400 disabled:cursor-not-allowed transition-all duration-300"
              >
                {isSaving ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  "Save Changes"
                )}
              </button>
            </form>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-4">
                  Personal Details
                </h3>
                <div className="space-y-3">
                  <p className="text-gray-600">
                    <span className="font-medium">ID Number:</span>{" "}
                    {driverData.id_number}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Plate Number:</span>{" "}
                    {driverData.car_plate_number}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Car Model:</span>{" "}
                    {driverData.car_model}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Phone Number:</span>{" "}
                    {driverData.phone_number}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Physical Address:</span>{" "}
                    {driverData.physical_address}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-4">Documents</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">License</p>
                    <img
                      src={driverData.license_image_url}
                      alt="License"
                      className="h-32 w-full object-cover rounded-lg"
                    />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">ID Document</p>
                    <img
                      src={driverData.id_image_url}
                      alt="ID"
                      className="h-32 w-full object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Vehicle Images Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            Vehicle Images
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(driverData.car_images).map(([key, url], index) => (
              <CarImageUpdate
                key={key}
                index={index}
                currentUrl={url}
                profileId={driverData.id}
                onUpdate={() => window.location.reload()}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
